import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import MessageDialog from "../../components/MessageDialog";

export default function AddProjectCategory() {
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [oldId, setOldId] = useState(null);
  // const messageTimer = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (id) {
      loadCategory(id);
    }
  }, [id]);

  const loadCategory = async (categoryId) => {
    try {
      setEditLoading(true); // 🔄 START LOADING
      setLoading(true);

      const res = await ProjectCategoryService.getById(categoryId);

      if (!res) {
        showMessage("Record not found!");
        return;
      }

      setValue("categoryName", res.categoryName || "");
      setValue("categoryCode", res.categoryCode || "");
      setValue("status", res.status || "Active");

      setEditMode(true);
      setOldId(categoryId);
    } catch (err) {
      console.error(err);
      showMessage("Error loading category");
    } finally {
      setLoading(false);
      setEditLoading(false); // ✅ STOP LOADING
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  // const closeMessage = () => {
  //   setMessage("");
  //   if (messageTimer.current) clearTimeout(messageTimer.current);
  // };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        categoryName: data.categoryName.trim(),
        categoryCode: data.categoryCode.trim(),
        createdBy: StorageService.loginData().employeeId,
        status: data.status || "Active",
        isActive: (data.status || "Active") === "Active" ? 1 : 0,
      };

      let res;
      if (editMode && oldId) {
        payload.updatedBy = StorageService.loginData().employeeId;
        res = await ProjectCategoryService.update(oldId, payload);
      } else {
        res = await ProjectCategoryService.create(payload);
      }

      if (res?.data || res?.id) {
        showMessage(
          editMode
            ? "Project Category updated successfully."
            : "Project Category added successfully.",
          "success"
        );
        setTimeout(() => navigate("/cpnl/categories/view"), 1000);
      } else if (
        res?.message &&
        res.message.toLowerCase().includes("duplicate")
      ) {
        showMessage("Duplicate Category Name or Code!", "error");
      } else {
        showMessage("Failed to save Project Category.", "error");
      }
    } catch (error) {
      console.error(error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Error saving Project Category";
      showMessage(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    localStorage.removeItem("editCategory");
    navigate("/cpnl/categories/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={
          editMode ? "Modify Project Category" : "Add Project Category"
        }
        secondTitle="Masters"
      />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {/* {message && (
            <div
              className={`alert ${msgType === "success" ? "alert-success" : "alert-danger"
                } d-flex justify-content-between align-items-center w-100 mb-3 p-2`}
            >
              <span>{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={closeMessage}
              ></button>
            </div>
          )} */}
          
            <MessageDialog
              message={message}
              type={msgType}
              setMessage={setMessage}
              setMsgType={setMsgType}
            />
          

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading data...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.categoryName ? "is-invalid" : ""
                      }`}
                    placeholder="Enter Category Name"
                    disabled={loading}
                    {...register("categoryName", {
                      required: "Category Name is required",
                      minLength: { value: 2, message: "Minimum 2 characters" },
                      maxLength: { value: 50, message: "Maximum 50 characters" },
                    })}
                  />
                  {errors.categoryName && (
                    <small className="text-danger">
                      {errors.categoryName.message}
                    </small>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Category Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.categoryCode ? "is-invalid" : ""
                      }`}
                    placeholder="Enter Category Code"
                    disabled={loading}
                    {...register("categoryCode", {
                      required: "Category Code is required",
                      // minLength: { value: 1, message: "Minimum 1 character" },
                      // maxLength: { value: 10, message: "Maximum 10 characters" },
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.categoryCode && (
                    <small className="text-danger">
                      {errors.categoryCode.message}
                    </small>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="d-inline-flex btn btn-primary me-2"
                  disabled={loading}
                >
                  <i className="ti ti-circle-check me-1"></i>{" "}
                  {loading
                    ? editMode
                      ? "Updating..."
                      : "Saving..."
                    : editMode
                      ? "Update"
                      : "Save"}
                </button>

                <button
                  type="button"
                  className="d-inline-flex btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="ti ti-cancel me-1"></i> Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
